import React from "react";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceArea,
} from "recharts";
import { fmt } from "./parts";

const AXIS = { fontSize: 11, fill: "#a1a1aa" };
const shortDate = (d) => { const x = new Date(d); return `${x.getDate()}/${x.getMonth() + 1}`; };

function Card({ children }) {
  return <div className="rounded-2xl border border-zinc-100 bg-white p-2">{children}</div>;
}

const TT = ({ active, payload, label, suffix = "" }) =>
  active && payload?.length ? (
    <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-zinc-500">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-mono font-semibold" style={{ color: p.color || p.fill }}>{fmt(p.value)}{suffix}</p>
      ))}
    </div>
  ) : null;

export function TrendChart({ data, color = "#059669", suffix = "", windows = [] }) {
  const gid = `g-${color.replace("#", "")}`;
  return (
    <ResponsiveContainer width="100%" height={240}>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
        <XAxis dataKey="date" tickFormatter={shortDate} tick={AXIS} axisLine={false} tickLine={false} minTickGap={28} />
        <YAxis tick={AXIS} axisLine={false} tickLine={false} tickFormatter={(v) => fmt(v)} width={44} />
        <Tooltip content={<TT suffix={suffix} />} />
        {windows.map((w) => (
          <ReferenceArea key={w.id} x1={w.start} x2={w.end} fill={w.color} fillOpacity={0.08} stroke={w.color} strokeOpacity={0.25} />
        ))}
        <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2.5} fill={`url(#${gid})`} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function ContentTypeBar({ data }) {
  const COLORS = ["#059669", "#4F46E5", "#0EA5E9", "#F59E0B"];
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
        <XAxis dataKey="type" tick={AXIS} axisLine={false} tickLine={false} />
        <YAxis tick={AXIS} axisLine={false} tickLine={false} width={40} tickFormatter={(v) => `${v}%`} />
        <Tooltip content={<TT suffix="% eng." />} cursor={{ fill: "#fafafa" }} />
        <Bar dataKey="avgEngagement" radius={[8, 8, 0, 0]}>
          {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function CampaignImpactBar({ data }) {
  // before/during/after weekly reach comparison per campaign
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }} barGap={4}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
        <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} />
        <YAxis tick={AXIS} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => fmt(v)} />
        <Tooltip content={<TT />} cursor={{ fill: "#fafafa" }} />
        <Bar dataKey="before" name="Before" fill="#d4d4d8" radius={[6, 6, 0, 0]} />
        <Bar dataKey="during" name="During" fill="#059669" radius={[6, 6, 0, 0]} />
        <Bar dataKey="after" name="After" fill="#4F46E5" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function DonutChart({ data, nameKey = "label", valueKey = "pct" }) {
  const COLORS = ["#059669", "#4F46E5", "#0EA5E9", "#F59E0B", "#F43F5E", "#8B5CF6"];
  return (
    <div className="flex items-center gap-4">
      <ResponsiveContainer width="55%" height={180}>
        <PieChart>
          <Pie data={data} dataKey={valueKey} nameKey={nameKey} innerRadius={45} outerRadius={75} paddingAngle={2} stroke="none">
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip content={<TT suffix="%" />} />
        </PieChart>
      </ResponsiveContainer>
      <ul className="flex-1 space-y-1.5">
        {data.map((d, i) => (
          <li key={i} className="flex items-center gap-2 text-sm">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
            <span className="text-zinc-600">{d[nameKey]}</span>
            <span className="ml-auto font-mono font-semibold text-zinc-900">{d[valueKey]}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function HBars({ data, labelKey = "city", valueKey = "pct", color = "#059669" }) {
  const max = Math.max(...data.map((d) => d[valueKey]));
  return (
    <ul className="space-y-3">
      {data.map((d, i) => (
        <li key={i}>
          <div className="mb-1 flex justify-between text-sm">
            <span className="text-zinc-600">{d[labelKey]}</span>
            <span className="font-mono font-semibold text-zinc-900">{d[valueKey]}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-zinc-100">
            <div className="h-full rounded-full" style={{ width: `${(d[valueKey] / max) * 100}%`, background: color }} />
          </div>
        </li>
      ))}
    </ul>
  );
}
